import React from 'react';
import { Box, Typography, Chip, Stack, Grid } from '@mui/material';
import { TrainingInsights, barColor, ToneScore } from '../types';
import { ProgressBar } from '../../ui/ProgressBar';
import SectionShell from './SectionShell';

interface Props {
  data: TrainingInsights['toneAnalysis'];
}

const ScoreRow: React.FC<{ label: string; data: ToneScore; positive: boolean }> = ({
  label, data, positive,
}) => {
  // For negative qualities (defensiveness/confrontation), a HIGH score is
  // bad, so flip the color logic to highlight when these are elevated.
  const displayColor = positive
    ? barColor(data.score)
    : barColor(100 - data.score);

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
        <Typography variant="subtitle2">{label}</Typography>
        <Box display="flex" gap={1} alignItems="center">
          <Chip
            label={`${data.score}%`}
            size="small"
            color={displayColor}
          />
          <Typography variant="caption" color="text.secondary">
            confidence {data.confidenceLevel}%
          </Typography>
        </Box>
      </Box>
      <ProgressBar value={data.score} tone={displayColor} className="mb-1" />
      {data.explanation && (
        <Typography variant="caption" color="text.secondary">
          {data.explanation}
        </Typography>
      )}
    </Box>
  );
};

const TONE_INTRO = 'How the rep\u2019s tone came across during the session. Positive '
  + 'qualities (confidence, optimism) should be high; negative qualities '
  + '(defensiveness, confrontation) should be low.';

const ToneAnalysisSection: React.FC<Props> = ({ data }) => {
  if (!data) return null;

  return (
    <SectionShell title="Tone Analysis" summary={TONE_INTRO}>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="overline" color="success.main">
            Positive
          </Typography>
          <Stack spacing={2} mt={1}>
            <ScoreRow label="Confidence" data={data.positive.confidence} positive />
            <ScoreRow label="Optimism"   data={data.positive.optimism}   positive />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="overline" color="error.main">
            Negative
          </Typography>
          <Stack spacing={2} mt={1}>
            <ScoreRow label="Defensiveness" data={data.negative.defensiveness} positive={false} />
            <ScoreRow label="Confrontation" data={data.negative.confrontation} positive={false} />
          </Stack>
        </Grid>
      </Grid>

      {data.overallCoaching && (
        <Box mt={3} p={2} bgcolor="var(--info-tint)" borderRadius={1} border={1} borderColor="info.main">
          <Typography variant="subtitle2" color="info.main" gutterBottom>
            Coaching note
          </Typography>
          <Typography variant="body2">{data.overallCoaching}</Typography>
        </Box>
      )}
    </SectionShell>
  );
};

export default ToneAnalysisSection;
